<template>
  <page-loader>
    <filter-sidebar
      v-model="filterDrawerOpen"
      :loading="isLoading"
      :show-clear="$_queryHandler_hasQueryChanged"
      @clear="$_queryHandler_resetFilters"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-subheader class="mb-2">Details</v-subheader>
            <select-list v-model="query.params.site" :items="siteOptions" label="Site" hide-details clearable />
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.campaignType"
              :items="campaignTypeOptions"
              label="Campaign Type"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <select-list v-model="query.params.language" :items="languages" label="Language" hide-details clearable />
          </v-col>
          <v-col cols="12">
            <date-month-picker v-model="query.params.month" label="Month" clearable />
          </v-col>
          <v-col cols="12">
            <text-input v-model="query.params.numberOfLinks" type="number" label="Number of links" clearable />
          </v-col>
        </v-row>
      </v-container>
    </filter-sidebar>

    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-tabs v-model="tab" grow>
              <v-tab>Overview</v-tab>
              <v-tab>Targets</v-tab>
            </v-tabs>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-tabs-items v-model="tab">
              <v-tab-item>
                <v-row class="px-5 pt-5 mt-0">
                  <v-col sm="3">
                    <date-month-picker
                      v-model="query.params.overviewMonth"
                      label="Month"
                      :max="currentMonth"
                      outlined
                    />
                  </v-col>
                </v-row>
                <v-row class="pa-5 mt-0">
                  <v-col cols="6">
                    <v-card outlined>
                      <v-card-text class="text-center">
                        <h2 class="text-h2">
                          <div>{{ linkTotal }}</div>
                          <div class="text-h6">Total Links</div>
                        </h2>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined>
                      <v-card-text class="text-center">
                        <h2 class="text-h2">
                          <div>{{ avgRatingThisMonth }}</div>
                          <div class="text-h6">Average DR</div>
                        </h2>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-text class="pa-0">
                        <div class="text-h6 pa-5">By Site Group ({{ overviewMonthLabel }})</div>
                        <vue-frappe
                          v-if="linksBySiteGroup.dataset[0].values.length"
                          id="links-by-site-group"
                          :key="query.params.overviewMonth"
                          :labels="linksBySiteGroup.labels"
                          type="bar"
                          :height="250"
                          :colors="['#169c78', '#2d3a48']"
                          :data-sets="linksBySiteGroup.dataset"
                          :tooltip-options="chartTooltipOptions"
                        ></vue-frappe>
                        <div v-else class="text-caption pa-5">
                          No links found this month
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-text class="pa-0">
                        <div class="text-h6 pa-5">By Category ({{ overviewMonthLabel }})</div>
                        <vue-frappe
                          v-if="linksByCategoryThisMonth.dataset[0].values.length"
                          id="links-by-category"
                          :key="query.params.overviewMonth"
                          :labels="linksByCategoryThisMonth.labels"
                          type="bar"
                          :height="250"
                          :colors="['#169c78', '#2d3a48']"
                          :data-sets="linksByCategoryThisMonth.dataset"
                          :tooltip-options="chartTooltipOptions"
                        ></vue-frappe>
                        <div v-else class="text-caption pa-5">
                          No links found this month
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12">
                    <v-card outlined class="pt-4">
                      <v-card-text class="pa-0">
                        <v-row class="mx-2">
                          <v-col cols="8" class="pb-0">
                            <div class="text-h6">By Site ({{ overviewMonthLabel }})</div>
                          </v-col>
                          <v-col class="pb-0">
                            <select-list
                              v-model="selectedSiteGroup"
                              :items="siteGroupOptions"
                              label="Site Group"
                              class="mt-2"
                            />
                          </v-col>
                        </v-row>
                        <vue-frappe
                          v-if="linksBySiteGroupSites.dataset[0].values.length"
                          id="links-by-site-group-sites"
                          :key="query.params.overviewMonth"
                          :labels="linksBySiteGroupSites.labels"
                          type="bar"
                          :height="250"
                          :colors="['#169c78', '#2d3a48']"
                          :data-sets="linksBySiteGroupSites.dataset"
                          :tooltip-options="chartTooltipOptions"
                        ></vue-frappe>
                        <div v-else class="text-caption pa-5">
                          No links found this month for any sites in this site group. Try another site group :)
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col>
                    <v-card outlined>
                      <v-card-text class="pa-0">
                        <div class="text-h6 pa-5">Links in the last six months</div>
                        <vue-frappe
                          v-if="linksInLastSixMonths.dataset[0].values.length"
                          id="links-by-month"
                          :labels="linksInLastSixMonths.labels"
                          type="bar"
                          :height="250"
                          :colors="['#169c78', '#2d3a48']"
                          :data-sets="linksInLastSixMonths.dataset"
                          :tooltip-options="chartTooltipOptions"
                        ></vue-frappe>
                        <div v-else class="text-caption pa-5">
                          No links found in the last 6 months :(
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item>
                <div class="pa-2">
                  <filter-chips
                    :data="filterChipsData"
                    :outlined="false"
                    empty-label="Showing all targets"
                    @clear-chip="$_filterChips_clearChip"
                  >
                    <v-badge color="accent" bordered overlap :content="numberOfTargetFilters">
                      <v-btn color="accent" small depressed title="Filters" @click="filterDrawerOpen = true">
                        <v-icon small left>filter_alt</v-icon>
                        <span>Filters</span>
                      </v-btn>
                    </v-badge>
                  </filter-chips>
                </div>
                <v-data-table
                  :loading="isLoading"
                  :headers="targetHeaders"
                  :items="backlinkTargets"
                  sort-by="targetted_at"
                  sort-desc
                >
                  <template #item="{ item }">
                    <v-hover v-slot="{ hover }">
                      <tr>
                        <td>{{ getOptionKeyByValue(siteOptions, item.site_id) }}</td>
                        <td>{{ getOptionKeyByValue(campaignTypeOptions, item.campaign_type) }}</td>
                        <td>{{ fnsFormat(new Date(item.targetted_at), 'MMMM y') }}</td>
                        <td>{{ item.number_of_links }}</td>
                        <td>{{ getOptionKeyByValue(languages, item.language_id) }}</td>
                        <td class="text-right">
                          <div v-show="hover">
                            <more-menu>
                              <v-list-item :disabled="!isPermittedTo('update')" @click="onTargetRowEditClick(item)">
                                <v-list-item-title>
                                  <v-icon left small>edit</v-icon>
                                  <span>Edit</span>
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item :disabled="!isPermittedTo('create')" @click="onTargetRowRenewClick(item)">
                                <v-list-item-title>
                                  <v-icon left small>refresh</v-icon>
                                  <span>Renew</span>
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item :disabled="!isPermittedTo('create')" @click="onTargetRowDeleteClick(item)">
                                <v-list-item-title>
                                  <v-icon left small>delete</v-icon>
                                  <span>Delete</span>
                                </v-list-item-title>
                              </v-list-item>
                            </more-menu>
                          </div>
                        </td>
                      </tr>
                    </v-hover>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="Target" @click="showCreateTargetModal = true" />

    <!-- Create Modal -->
    <generic-modal
      v-model="showCreateTargetModal"
      :is-confirm-disabled="isCreateTargetsDisabled"
      title="Create Backlink Targets"
      persistent
      @modal-confirm="onCreateTargetsConfirm"
      @modal-cancel="onCreateTargetsCancel"
    >
      <v-container>
        <v-row>
          <v-col>
            <select-list v-model="newTargetSite" :items="siteOptions" label="Site" required />
          </v-col>
          <v-col>
            <date-month-picker v-model="newTargetMonth" label="Month" required />
          </v-col>
          <v-col>
            <select-list
              v-model="newTargetLanguage"
              :items="languageOptions"
              label="Language"
              :disabled="!newTargetSite"
              required
            />
          </v-col>
        </v-row>
        <v-card max-height="400" class="pa-4 overflow-auto" outlined>
          <v-row v-for="(target, index) in newTargets" :key="index">
            <v-col>
              <select-list
                v-model="newTargets[index].campaign_type"
                :items="campaignTypeOptions"
                label="Campaign Type"
                hide-details="auto"
                required
              />
            </v-col>
            <v-col>
              <text-input
                v-model.number="newTargets[index].number_of_links"
                type="number"
                hide-details="auto"
                label="Number of links"
                autocomplete="false"
                required
              />
            </v-col>
            <v-col cols="auto">
              <v-btn :disabled="newTargets.length === 1" icon @click="onDeleteRowClick(index)">
                <v-icon>delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-btn depressed color="light-blue" dark @click="onNewRowClick">Add new row</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
    </generic-modal>

    <!-- Edit Modal -->
    <generic-modal
      v-model="showEditTargetModal"
      :is-confirm-disabled="isEditTargetDisabled"
      :title="`${renewingTarget ? 'Renew' : 'Edit'} Backlink Target`"
      persistent
      @modal-confirm="onEditTargetConfirm"
      @modal-cancel="onEditTargetCancel"
    >
      <v-container>
        <v-row v-if="renewingTarget">
          <v-col class="pt-0">
            <span class="text-caption"
              >This will create a <strong>new</strong> target. The clicked target will be unaffected.</span
            >
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <select-list
              v-model="editedTarget.site_id"
              :items="siteOptions"
              label="Site"
              required
              :disabled="renewingTarget"
            />
          </v-col>
          <v-col>
            <date-month-picker v-model="editedTarget.targetted_at" label="Month" required />
          </v-col>
          <v-col>
            <select-list
              v-model="editedTarget.language_id"
              :items="languageOptions"
              label="Language"
              required
              :disabled="renewingTarget"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <select-list
              v-model="editedTarget.campaign_type"
              :items="campaignTypeOptions"
              label="Campaign Type"
              hide-details="auto"
              required
              :disabled="renewingTarget"
            />
          </v-col>
          <v-col>
            <text-input
              v-model.number="editedTarget.number_of_links"
              type="number"
              hide-details="auto"
              label="Number of links"
              autocomplete="false"
              required
            />
          </v-col>
        </v-row>
      </v-container>
    </generic-modal>

    <delete-modal
      v-model="showDeleteTargetModal"
      category="Backlink Target"
      item="target"
      @modal-confirm="onDeleteTargetConfirm"
      @modal-cancel="onDeleteTargetCancel"
    >
      <v-card outlined class="mt-4">
        <v-list dense>
          <v-list-item>
            <v-list-item-content>Site</v-list-item-content>
            <v-list-item-content>{{ getOptionKeyByValue(siteOptions, deletingTarget.site_id) }}</v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>Month</v-list-item-content>
            <v-list-item-content>{{
              fnsFormat(new Date(deletingTarget.targetted_at || 0), 'MMMM y')
            }}</v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>Campaign Type</v-list-item-content>
            <v-list-item-content>{{
              getOptionKeyByValue(campaignTypeOptions, deletingTarget.campaign_type)
            }}</v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>Number of links</v-list-item-content>
            <v-list-item-content>{{ deletingTarget.number_of_links }}</v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </delete-modal>
  </page-loader>
</template>

<script>
// Helpers
import { mapMutations, mapGetters } from 'vuex'
import { format as fnsFormat, set as fnsSet } from 'date-fns'
import { merge } from 'lodash-es'
import setPlural from '@/utils/set-plural'
import isSet from '@/utils/is-set'
import getOptionKeyByValue from '@/utils/get-option-key-by-value'
// Services
import { backlinks, backlinkTargets, languageSites, sites, siteGroups } from '@/services/offers'
// Mixins
import FilterChipsHandler from '@/mixins/filter-chips'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Components
import { VueFrappe } from 'vue2-frappe'
import DateMonthPicker from '@/components/date-month-picker'
import DeleteModal from '@/components/modals/delete-modal'
import FabCreate from '@/components/fab-create'
import FilterChips from '@/components/filter-chips'
import FilterSidebar from '@/components/filter-sidebar'
import GenericModal from '@/components/modals/generic-modal'
import MoreMenu from '@/components/more-menu'
import PageLoader from '@/components/page-loader'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input.vue'

const newTarget = () => ({
  campaign_type: null,
  number_of_links: null,
})

const currentMonth = () => fnsFormat(new Date(), 'y-M')

export default {
  name: 'BacklinkDashboard',
  components: {
    DateMonthPicker,
    DeleteModal,
    FabCreate,
    FilterChips,
    FilterSidebar,
    GenericModal,
    MoreMenu,
    PageLoader,
    SelectList,
    TextInput,
    VueFrappe,
  },
  mixins: [FilterChipsHandler, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    backlinkTargets: [],
    currentMonth: currentMonth(),
    defaultQuery: {
      overviewMonth: currentMonth(),
    },
    deletingTarget: {},
    editedTarget: {},
    filterDrawerOpen: false,
    languages: [],
    links: [],
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    newTargets: [newTarget()],
    newTargetLanguage: null,
    newTargetMonth: null,
    newTargetSite: null,
    queryDefaults: ['overviewMonth'],
    queriesDefined: ['site', 'campaignType', 'numberOfLinks', 'month', 'overviewMonth'],
    queryTypes: { site: 'number' },
    renewingTarget: false,
    selectedSiteGroup: null,
    siteGroups: [],
    showCreateTargetModal: false,
    showDeleteTargetModal: false,
    showEditTargetModal: false,
    siteData: [],
    tab: 0,
    chartTooltipOptions: {
      formatTooltipX: d => String(d).toUpperCase(),
      formatTooltipY: d => (d === 1 ? d + ' Link' : d + ' Links'),
    },
  }),
  computed: {
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
    }),
    campaignTypeOptions() {
      return this.getItemsForOption('campaignTypes')
    },
    isCreateTargetsDisabled() {
      return !(
        this.newTargetSite &&
        this.newTargetMonth &&
        this.newTargetLanguage &&
        this.newTargets.every(({ campaign_type, number_of_links }) => campaign_type && number_of_links)
      )
    },
    isEditTargetDisabled() {
      const { site_id, targetted_at, number_of_links, campaign_type } = this.editedTarget
      return ![site_id, targetted_at, number_of_links, campaign_type].every(v => isSet(v))
    },
    filterChipsData() {
      return [
        {
          label: 'Search',
          model: 'query.searchTerm',
          value: this.query.searchTerm,
        },
        {
          label: 'Site',
          model: 'query.params.site',
          options: this.siteOptions,
          value: this.query.params.site,
        },
        {
          label: 'Campaign Type',
          model: 'query.params.campaignType',
          options: this.campaignTypeOptions,
          value: this.query.params.campaignType,
        },
        {
          label: 'Month',
          model: 'query.params.month',
          format: 'month-year',
          value: this.query.params.month,
        },
        {
          label: 'Number of links',
          model: 'query.params.numberOfLinks',
          value: this.query.params.numberOfLinks,
        },
      ]
    },
    languageOptions() {
      return this.languages.filter(
        ({ site_id }) => site_id === this.newTargetSite || site_id === this.editedTarget.site_id
      )
    },
    linksBySiteGroup() {
      const { overviewMonth } = this.query.params

      const filteredLinksBySiteGroup = this.links
        .filter(({ live_date }) => this.isSameMonth(live_date, overviewMonth))
        .reduce((acc, link) => {
          const { name } = this.siteGroups.find(({ id }) => id === link.targetSite.site_group_id) || {}
          const siteGroup = name ?? 'No Site Group'
          if (!acc[siteGroup]) acc[siteGroup] = { total: 0 }
          acc[siteGroup].total += 1
          return acc
        }, {})

      const filteredTargetsBySiteGroup = this.backlinkTargets
        .filter(target => this.isSameMonth(target.targetted_at, overviewMonth))
        .reduce((acc, { site: { site_group_id }, number_of_links }) => {
          const { name } = this.siteGroups.find(({ id }) => id === site_group_id) || {}
          const siteGroup = name ?? 'No Site Group'
          if (!acc[siteGroup]) acc[siteGroup] = { target: 0 }
          acc[siteGroup].target += number_of_links
          return acc
        }, {})

      const mergedLinksAndTargets = merge(filteredLinksBySiteGroup, filteredTargetsBySiteGroup)

      const chartObject = {
        labels: [],
        dataset: [
          { values: [], name: 'Links acquired', chartType: 'bar' },
          { values: [], name: 'Target', chartType: 'bar' },
        ],
      }

      Object.entries(mergedLinksAndTargets).forEach(([siteGroup, { total = 0, target = 0 }]) => {
        chartObject.labels.push(siteGroup)
        chartObject.dataset[0].values.push(total)
        chartObject.dataset[1].values.push(target)
      })

      return chartObject
    },
    linksBySiteGroupSites() {
      const { overviewMonth } = this.query.params

      const filteredLinksBySite = this.links
        .filter(({ live_date, targetSite: { site_group_id } = {} }) => {
          return this.isSameMonth(live_date, overviewMonth) && site_group_id === this.selectedSiteGroup
        })
        .reduce((acc, link) => {
          const { key } = this.siteOptions.find(({ value }) => value === link.target_site_id) || {}
          const site = key ?? 'No Site Name'
          if (!acc[site]) acc[site] = { total: 0 }
          acc[site].total += 1
          return acc
        }, {})

      const filteredTargetsBySite = this.backlinkTargets
        .filter(({ targetted_at, site: { site_group_id } = {} }) => {
          return this.isSameMonth(targetted_at, overviewMonth) && site_group_id === this.selectedSiteGroup
        })
        .reduce((acc, { site: { id }, number_of_links }) => {
          const { key } = this.siteOptions.find(({ value }) => value === id) || {}
          const site = key ?? 'No Site Name'
          if (!acc[site]) acc[site] = { target: 0 }
          acc[site].target += number_of_links
          return acc
        }, {})

      const mergedLinksAndTargets = merge(filteredLinksBySite, filteredTargetsBySite)

      const chartObject = {
        labels: [],
        dataset: [
          { values: [], name: 'Links acquired', chartType: 'bar' },
          { values: [], name: 'Target', chartType: 'bar' },
        ],
      }

      Object.entries(mergedLinksAndTargets).forEach(([categorySlug, { total = 0, target = 0 }]) => {
        chartObject.labels.push(categorySlug)
        chartObject.dataset[0].values.push(total)
        chartObject.dataset[1].values.push(target)
      })
      return chartObject
    },
    linkTotal() {
      return this.links.filter(({ live_date }) => this.isSameMonth(live_date, this.query.params.overviewMonth)).length
    },
    linksInLastSixMonths() {
      const sixMonthsAgo = this.getDateNMonthsAgo(new Date(currentMonth()), 5)
      const nextMonth = fnsSet(new Date(), {
        month: new Date(currentMonth()).getMonth() + 1,
        date: 1,
        hours: 0,
        minutes: 0,
        seconds: 0,
      })

      const filteredLinksByMonth = this.links
        .filter(
          ({ live_date }) =>
            new Date(live_date) >= sixMonthsAgo &&
            new Date(live_date) < nextMonth &&
            fnsFormat(new Date(live_date), 'y-M') !== fnsFormat(nextMonth, 'y-M')
        )
        .reduce((acc, link) => {
          const backlinkDate = new Date(link.live_date)
          const backlinkMonth = this.months[backlinkDate.getMonth()]
          if (!acc[backlinkMonth]) acc[backlinkMonth] = { total: 0 }
          acc[backlinkMonth].total += 1
          return acc
        }, {})

      const filteredTargetsByMonth = this.backlinkTargets
        .filter(
          ({ targetted_at }) =>
            new Date(targetted_at) >= sixMonthsAgo &&
            new Date(targetted_at) < nextMonth &&
            fnsFormat(new Date(targetted_at), 'y-M') !== fnsFormat(nextMonth, 'y-M')
        )
        .reduce((acc, { number_of_links, targetted_at }) => {
          const targetDate = new Date(targetted_at)
          const targetMonth = this.months[targetDate.getMonth()]
          if (!acc[targetMonth]) acc[targetMonth] = { target: 0 }
          acc[targetMonth].target += number_of_links
          return acc
        }, {})

      const mergedLinksAndTargets = merge(filteredLinksByMonth, filteredTargetsByMonth)

      const chartObject = {
        labels: [],
        dataset: [
          { values: [], name: 'Links acquired', chartType: 'bar' },
          { values: [], name: 'Target', chartType: 'bar' },
        ],
      }

      Object.entries(mergedLinksAndTargets).forEach(([month, { total = 0, target = 0 }]) => {
        chartObject.labels.push(month)
        chartObject.dataset[0].values.push(total)
        chartObject.dataset[1].values.push(target)
      })

      return chartObject
    },
    avgRatingThisMonth() {
      return Math.round(
        this.links
          .filter(backlink => {
            return this.isSameMonth(backlink.live_date, this.query.params.overviewMonth)
          })
          .reduce(
            ({ avg, n }, backlink) => {
              return { avg: (backlink.domain_rating + n * avg) / (n + 1), n: n + 1 }
            },
            { avg: 0, n: 0 }
          ).avg
      )
    },
    linksByCategoryThisMonth() {
      const { overviewMonth } = this.query.params
      const filteredLinksByCategory = this.links
        .filter(backlink => this.isSameMonth(backlink.live_date, overviewMonth))
        .reduce((acc, backlink) => {
          if (!acc[backlink.campaign_type]) acc[backlink.campaign_type] = { total: 0 }
          acc[backlink.campaign_type].total += 1
          return acc
        }, {})

      const filteredTargetsByCategory = this.backlinkTargets
        .filter(target => this.isSameMonth(target.targetted_at, overviewMonth))
        .reduce((acc, { campaign_type, number_of_links }) => {
          if (!acc[campaign_type]) acc[campaign_type] = { target: 0 }
          acc[campaign_type].target += number_of_links
          return acc
        }, {})

      const mergedLinksAndTargets = merge(filteredLinksByCategory, filteredTargetsByCategory)

      const chartObject = {
        labels: [],
        dataset: [
          { values: [], name: 'Links acquired', chartType: 'bar' },
          { values: [], name: 'Target', chartType: 'bar' },
        ],
      }
      Object.entries(mergedLinksAndTargets).forEach(([categorySlug, { total = 0, target = 0 }]) => {
        const categoryName = this.campaignTypeOptions.find(({ value }) => value === categorySlug)?.key
        chartObject.labels.push(categoryName ?? 'No Category')
        chartObject.dataset[0].values.push(total)
        chartObject.dataset[1].values.push(target)
      })

      return chartObject
    },
    numberOfTargetFilters() {
      // Subtract the overview-month filter from total queries.
      return String(Number(this.$_queryHandler_numberOfQueries) - 1)
    },
    overviewMonthLabel() {
      const { overviewMonth } = this.query.params
      if (overviewMonth === currentMonth()) return 'this month'
      return `${fnsFormat(new Date(overviewMonth), 'MMM y')}`
    },
    siteOptions() {
      return this.siteData.map(({ name, id }) => ({ key: name, value: id }))
    },
    siteGroupOptions() {
      return this.siteGroups.map(({ id, name }) => ({ key: name, value: id }))
    },
    targetHeaders() {
      const {
        site: siteFilter,
        campaignType: campaignFilter,
        numberOfLinks: numberFilter,
        month: monthFilter,
        language: languageFilter,
      } = this.query.params

      const newMonthStart = date => fnsSet(new Date(date), { date: 1, hours: 0, minutes: 0 })

      return [
        { text: 'Site', value: 'site_id', filter: v => !siteFilter || v === siteFilter },
        { text: 'Campaign Type', value: 'campaign_type', filter: v => !campaignFilter || v === campaignFilter },
        {
          text: 'Month',
          value: 'targetted_at',
          filter: v => !monthFilter || String(newMonthStart(v)) === String(newMonthStart(monthFilter)),
        },
        {
          text: 'Number of Links',
          value: 'number_of_links',
          filter: v => !numberFilter || v === Number(numberFilter),
        },
        {
          text: 'Language',
          value: 'language_id',
          filter: v => !languageFilter || v === languageFilter,
        },
        { text: '', value: 'actions', sortable: false, align: 'right', width: '70' },
      ]
    },
  },
  watch: {
    siteGroupOptions: {
      immediate: true,
      handler() {
        if (this.siteGroupOptions.length) this.selectedSiteGroup = this.siteGroupOptions[0].value
      },
    },
    newTargetSite() {
      this.getLanguageSites()
    },
    'editedTarget.site_id'(value) {
      if (value) this.getLanguageSites()
    },
  },
  async created() {
    await Promise.all([
      this.fetchLinks(),
      this.getSiteData(),
      this.getBacklinkTargets(),
      this.getLanguageSites(),
      this.getSiteGroups(),
    ])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    getOptionKeyByValue,
    fnsFormat,
    async createBacklinkTargets() {
      this.isLoading = true
      try {
        const targets = this.transformNewTargets()
        await Promise.all(targets.map(async target => backlinkTargets.create(target)))
          .then(() => {
            this.$notify({
              type: 'success',
              text: `Successfully create backlink ${setPlural(targets.length, 'target')}`,
            })
          })
          .catch(error =>
            this.$notify({
              type: 'error',
              text: 'Failed to create one or more backlink targets',
              data: { error },
              duration: -1,
            })
          )
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to create backlink targets',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async deleteTarget() {
      this.isLoading = true
      try {
        await backlinkTargets.delete(this.deletingTarget.id)
        this.$notify({
          type: 'success',
          text: 'Successfully deleted backlink target',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete backlink target',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    getDateNMonthsAgo(currentDate, numberOfMonthsAgo) {
      return fnsSet(new Date(), {
        month: currentDate.getMonth() - numberOfMonthsAgo,
        date: 1,
        hours: 0,
        minutes: 0,
        seconds: 0,
      })
    },
    async fetchLinks() {
      this.isLoading = true
      try {
        const { data } = await backlinks.getBacklinks()
        this.links = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch backlinks',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getBacklinkTargets() {
      this.isLoading = true
      try {
        this.backlinkTargets = await backlinkTargets.getAll()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch backlink targets',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getLanguageSites() {
      this.isLoading = true
      try {
        const response = await languageSites.getAll()
        this.languages = response.map(({ site_id, language: { name }, language_id }) => {
          return { key: name, value: language_id, site_id }
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch available languages',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getSiteGroups() {
      this.isLoading = true
      const { data } = await siteGroups.getAll({ paginate: { perPage: null, page: 1 } })
      try {
        this.siteGroups = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch site groups',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getSiteData() {
      this.isloading = true
      try {
        const { data } = await sites.getAllSites()
        this.siteData = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch sites',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    isSameMonth(a, b) {
      return fnsFormat(new Date(a), 'y-M') === fnsFormat(new Date(b), 'y-M')
    },
    onCreateTargetsCancel() {
      this.newTargetSite = null
      this.newTargetMonth = null
      this.newTargetLanguage = null
      this.newTargets = [newTarget()]
    },
    async onCreateTargetsConfirm() {
      await this.createBacklinkTargets()
      this.tab = 1
      this.showCreateTargetModal = false
      await this.getBacklinkTargets()
    },
    onDeleteRowClick(index) {
      const updatedTargets = this.newTargets.slice(0, index).concat(this.newTargets.slice(index + 1))
      this.$set(this, 'newTargets', updatedTargets)
    },
    async onDeleteTargetConfirm() {
      await this.deleteTarget()
      this.deletingTarget = {}
      this.showDeleteTargetModal = false
      await this.getBacklinkTargets()
    },
    onDeleteTargetCancel() {
      this.deletingTarget = {}
      this.showDeleteTargetModal = false
    },
    async onEditTargetConfirm() {
      this.renewingTarget ? await this.renewBacklinkTarget() : await this.updateBacklinkTarget()
      this.showEditTargetModal = false
      this.editedTarget = {}
      await this.getBacklinkTargets()
    },
    onEditTargetCancel() {
      this.showEditTargetModal = false
      this.editedTarget = {}
      this.renewingTarget = true
    },
    onNewRowClick() {
      const updatedTargets = [...this.newTargets, newTarget()]
      this.$set(this, 'newTargets', updatedTargets)
    },
    onTargetRowDeleteClick(item) {
      this.deletingTarget = { ...item }
      this.showDeleteTargetModal = true
    },
    onTargetRowEditClick(item) {
      this.editedTarget = { ...item }
      this.showEditTargetModal = true
    },
    onTargetRowRenewClick(item) {
      this.renewingTarget = true
      this.onTargetRowEditClick(item)
    },
    async renewBacklinkTarget() {
      this.isLoading = true
      try {
        await backlinkTargets.create({
          site_id: this.editedTarget.site_id,
          language_id: this.editedTarget.language_id,
          targetted_at: new Date(this.editedTarget.targetted_at),
          campaign_type: this.editedTarget.campaign_type,
          number_of_links: this.editedTarget.number_of_links,
        })
        this.$notify({
          type: 'success',
          text: 'Successfully renewed backlink target',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to renew backlink target',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    transformNewTargets() {
      return this.newTargets.map(target => ({
        ...target,
        site_id: this.newTargetSite,
        language_id: this.newTargetLanguage,
        targetted_at: new Date(this.newTargetMonth),
      }))
    },
    async updateBacklinkTarget() {
      this.isLoading = true
      try {
        await backlinkTargets.update(this.editedTarget.id, this.editedTarget)
        this.$notify({
          type: 'success',
          text: 'Successfully updated backlink target',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to update backlink target',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}
</style>
